import { ReactComponent as IntroIcon } from "assets/icons/intro-icon.svg";
import { ReactComponent as CheatSheetIcon } from "assets/icons/nesting-cheat-sheet-icon.svg";
import { ReactComponent as DocIcon } from "assets/icons/doc-icon.svg";
import { ReactComponent as ExamplesIcon } from "assets/icons/ssml-examples-icon.svg";
import { useTranslation } from "react-i18next";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { Box, Card, CardContent, Grid, Typography } from "@mui/material";
import { useState, RefObject } from "react";
import IntroductionTour from "./IntroductionTour";
import CustomDialogIntroductionTour from "shared/components/CustomDialogIntroductionTour";
import SSMLExampleGrid from "@/shared/components/CustomDialogExampleScenarios";
import { TTSCustomEditorHandle } from "../TTSCustomEditor";

interface Props {
  editorRef: RefObject<TTSCustomEditorHandle>;
}

export default function TTSHelpAndLearning({ editorRef }: Props) {
  const { t } = useTranslation();
  const [openIntroDialog, setOpenIntroDialog] = useState(false);
  const [openExamplesDialog, setOpenExamplesDialog] = useState(false);
  const [insertedExamples, setInsertedExamples] = useState<Set<string>>(
    new Set()
  );

  const helpAndLearningCards = [
    {
      icon: <IntroIcon />,
      title: t("tts.helpAndLearning.button1"),
      description: t("tts.helpAndLearning.text1"),
      url: "/introduction-tour",
      action: "intro-dialog",
    },
    {
      icon: <CheatSheetIcon />,
      title: t("tts.helpAndLearning.button2"),
      description: t("tts.helpAndLearning.text2"),
      url: "/ssml-cheat-sheet",
      action: "external",
    },
    {
      icon: <DocIcon />,
      title: t("tts.helpAndLearning.button3"),
      description: t("tts.helpAndLearning.text3"),
      url: "/ssml-guide",
      action: "external",
    },
    {
      icon: <ExamplesIcon />,
      title: t("tts.helpAndLearning.button4"),
      description: t("tts.helpAndLearning.text4"),
      url: "/example-scenarios",
      action: "examples-dialog",
    },
  ];

  const handleCardClick = (card: (typeof helpAndLearningCards)[0]) => {
    if (card.action === "intro-dialog") {
      setOpenIntroDialog(true);
    } else if (card.action === "examples-dialog") {
      // Reset inserted examples tracking if the editor is now empty
      if (editorRef.current?.isEmpty()) {
        setInsertedExamples(new Set());
      }
      setOpenExamplesDialog(true);
    } else {
      window.open(card.url, "_blank", "noopener,noreferrer");
    }
  };

  const handleCloseIntroDialog = () => {
    setOpenIntroDialog(false);
  };

  const handleCloseExamplesDialog = () => {
    setOpenExamplesDialog(false);
  };

  const handleInsertExample = (ssml: string) => {
    editorRef.current?.insertExample(ssml);
    setInsertedExamples((prev) => new Set(prev).add(ssml));
  };

  return (
    <>
      <Box sx={{ flexGrow: 1, mt: 8 }}>
        <Typography variant="h4" id="help-and-learning-header">
          {t("tts.helpAndLearning.title")}
        </Typography>
      </Box>
      <Grid container spacing={3} mt={1}>
        {helpAndLearningCards.map((card, index) => (
          <Grid
            item
            xs={12}
            sm={12}
            md={6}
            lg={4}
            key={card.title || index}
            onClick={() => handleCardClick(card)}
            sx={{ cursor: "pointer" }}
          >
            <Card
              sx={{
                padding: 0,
                height: "88px",
                minWidth: "325px",
                "&:hover": {
                  backgroundColor: "action.hover",
                },
              }}
            >
              <CardContent
                sx={{
                  padding: 0,
                  height: "100%",
                  "&:last-child": {
                    paddingBottom: 0,
                  },
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    border: "1px solid rgba(0, 0, 0, 0.12)",
                    padding: "16px",
                    height: "100%",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      flex: 1,
                    }}
                  >
                    {/* Icon */}
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      {card.icon}
                    </Box>

                    {/* Content */}
                    <Box sx={{ flex: 1 }}>
                      <Typography
                        variant="subtitle1"
                        sx={{
                          fontWeight: 500,
                          marginBottom: 0.5,
                        }}
                      >
                        {card.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {card.description}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Arrow */}
                  <ArrowForwardIcon
                    sx={{
                      color: "text.secondary",
                      marginLeft: 1,
                    }}
                  />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Introduction Tour Dialog */}
      <CustomDialogIntroductionTour
        open={openIntroDialog}
        showDivider={false}
        showCloseIcon
        header=""
        handleOnClose={handleCloseIntroDialog}
        maxWidth="xl"
        height="auto"
        hideActions
        hideTitleBar
      >
        <IntroductionTour onFinish={handleCloseIntroDialog} />
      </CustomDialogIntroductionTour>

      {/* Example Scenarios Dialog */}
      <SSMLExampleGrid
        open={openExamplesDialog}
        handleOnClose={handleCloseExamplesDialog}
        onInsertExample={handleInsertExample}
        insertedExamples={insertedExamples}
      />
    </>
  );
}
